'use client';

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Fragment, useMemo, useState } from 'react';

import { Checkbox } from './checkbox';

export interface MultiListboxOption {
  value: string;
  label: string;
}

interface MultiListboxProps {
  options: MultiListboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * A premium multi-select component with search, select all, and clear functionality.
 * Styled to match the Catalyst design system.
 */
export const MultiListbox = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  disabled = false,
  'aria-label': ariaLabel,
}: MultiListboxProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Get display text for the button
  const displayText = useMemo(() => {
    if (value.length === 0) return null;
    if (value.length === 1) {
      const selected = options.find((opt) => opt.value === value[0]);
      return selected?.label;
    }
    return `${value.length} selected`;
  }, [value, options]);

  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    const allFilteredValues = filteredOptions.map((opt) => opt.value);
    // Merge with existing selections (for items not in current filter)
    const existingNotInFilter = value.filter(
      (v) => !filteredOptions.some((opt) => opt.value === v),
    );
    onChange([...existingNotInFilter, ...allFilteredValues]);
  };

  const handleClear = () => {
    // Only clear items that are currently visible in the filter
    const filteredValues = filteredOptions.map((opt) => opt.value);
    const remaining = value.filter((v) => !filteredValues.includes(v));
    onChange(remaining);
  };

  const selectedInView = filteredOptions.filter((opt) =>
    value.includes(opt.value),
  ).length;

  return (
    <Popover className="relative w-full" data-slot="control">
      {({ open }) => (
        <>
          <PopoverButton
            disabled={disabled}
            aria-label={ariaLabel}
            className={clsx([
              className,
              // Basic layout
              'group relative block w-full text-left',
              // Background color + shadow applied to inset pseudo element
              'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
              'dark:before:hidden',
              // Hide default focus styles
              'focus:outline-hidden',
              // Focus ring
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-blue-500',
              // Disabled state
              'data-disabled:opacity-50 data-disabled:before:bg-zinc-950/5 data-disabled:before:shadow-none',
            ])}
          >
            <span
              className={clsx([
                // Basic layout
                'relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
                // Set minimum height
                'min-h-11 sm:min-h-9',
                // Horizontal padding
                'pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]',
                // Typography
                'text-base/6 sm:text-sm/6',
                displayText
                  ? 'text-zinc-950 dark:text-white'
                  : 'text-zinc-500 dark:text-zinc-400',
                // Border
                'border border-zinc-950/10 group-data-active:border-zinc-950/20 group-data-hover:border-zinc-950/20 dark:border-white/10 dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20',
                // Background color
                'bg-transparent dark:bg-white/5',
                // Active state
                open && 'border-zinc-950/20 dark:border-white/20',
              ])}
            >
              <span className="block truncate">
                {displayText ?? placeholder}
              </span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                className={clsx(
                  'size-5 stroke-zinc-500 sm:size-4 dark:stroke-zinc-400',
                  'transition-transform duration-200',
                  open && 'rotate-180',
                )}
                viewBox="0 0 16 16"
                aria-hidden="true"
                fill="none"
              >
                <path
                  d="M5.75 10.75L8 13L10.25 10.75"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.25 5.25L8 3L5.75 5.25"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </PopoverButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            afterLeave={() => setSearchQuery('')}
          >
            <PopoverPanel
              anchor="bottom start"
              className={clsx(
                // Positioning
                'absolute z-50 mt-2',
                // Base styles
                'w-72 rounded-xl p-0 select-none overflow-hidden',
                // Background
                'bg-white dark:bg-zinc-900',
                // Shadows & border
                'shadow-xl ring-1 ring-zinc-950/10 dark:ring-white/10',
              )}
            >
              {/* Header with actions */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-950/5 dark:border-white/5">
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    Select all
                  </button>
                  <span className="text-zinc-300 dark:text-zinc-600">-</span>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300 transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <span className="text-xs text-zinc-400 dark:text-zinc-500">
                  {selectedInView > 0
                    ? `${selectedInView} of ${filteredOptions.length}`
                    : `${filteredOptions.length} items`}
                </span>
              </div>

              {/* Search input */}
              <div className="px-3 py-2 border-b border-zinc-950/5 dark:border-white/5">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 dark:text-zinc-500" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={clsx(
                      'w-full h-9 pl-9 pr-3 text-sm rounded-lg',
                      'bg-zinc-50 dark:bg-zinc-800/50',
                      'border border-zinc-200 dark:border-zinc-700',
                      'text-zinc-900 dark:text-zinc-100',
                      'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
                      'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'transition-all duration-150',
                    )}
                  />
                </div>
              </div>

              {/* Options list */}
              <div className="max-h-64 overflow-y-auto py-1">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-6 text-center">
                    <p className="text-sm text-zinc-400 dark:text-zinc-500">
                      No results found
                    </p>
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleToggle(option.value)}
                        className={clsx(
                          'w-full flex items-center gap-3 px-3 py-2 text-left',
                          'transition-colors duration-100',
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-500/10'
                            : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {}}
                          className="pointer-events-none size-4"
                          color={isSelected ? 'blue' : 'zinc'}
                        />
                        <span
                          className={clsx(
                            'text-sm truncate',
                            isSelected
                              ? 'text-zinc-900 dark:text-white font-medium'
                              : 'text-zinc-700 dark:text-zinc-300',
                          )}
                        >
                          {option.label}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
